/**
 * @fileoverview Chat list filtering, sorting, and display utilities.
 * @module logic/chat/chatListUtils
 */

/**
 * Sorts chats by last message timestamp (most recent first).
 * @param {Array} chats - Array of chat objects
 * @returns {Array} Sorted array of chats
 */
export const getSortedChats = (chats) => {
  return [...chats].sort((a, b) => {
    const dateA = new Date(a.lastMessageCreatedAt);
    const dateB = new Date(b.lastMessageCreatedAt);
    return dateB - dateA;
  });
};

/**
 * Filters and sorts chats based on search query.
 * @param {Array} chats - Array of chat objects
 * @param {string} searchQuery - Search string to filter by
 * @param {Array} users - Array of user objects
 * @param {Object} companies - Companies data keyed by RFC
 * @returns {Array} Filtered and sorted array of chats
 */
export const getFilteredChats = (chats, searchQuery, users, companies) => {
  if (!searchQuery.trim()) {
    return getSortedChats(chats);
  }

  const query = searchQuery.toLowerCase().trim();
  const currentUserId = localStorage.getItem("userId");

  return getSortedChats(chats).filter((chat) => {
    const otherUserId =
      chat.userAId === currentUserId ? chat.userBId : chat.userAId;
    const matchedUser = users.find((user) => user._id === otherUserId);

    if (!matchedUser) {
      return false;
    }

    // Search by user name
    const userName = matchedUser.nombre?.toLowerCase() || "";
    if (userName.includes(query)) {
      return true;
    }

    // Search by user email
    const userEmail = matchedUser.correoElectronico?.toLowerCase() || "";
    if (userEmail.includes(query)) {
      return true;
    }

    // Search by company name from companies data
    if (matchedUser.RFC && companies[matchedUser.RFC]) {
      const companyName =
        companies[matchedUser.RFC].nombreComercial?.toLowerCase() || "";
      if (companyName.includes(query)) {
        return true;
      }
    }

    return false;
  });
};

/**
 * Checks if a user is currently online.
 * @param {string} userId - User identifier to check
 * @param {Array} activeConnections - Array of active connections
 * @returns {boolean} True if user is online
 */
export const isUserOnline = (userId, activeConnections) => {
  return activeConnections.some((connection) => connection.userId === userId);
};

/**
 * Counts the number of unread chats.
 * @param {Array} chats - Array of chat objects
 * @returns {number} Number of unread chats
 */
export const getUnreadChatsCount = (chats) => {
  return chats.filter((chat) => chat.lastMessageSeen === false).length;
};

/**
 * Returns appropriate search bar width based on device type.
 * @param {string} device - Device type (tablet, mediumMobile, smallMobile)
 * @returns {string} CSS width value
 */
export const getSearchBarWidth = (device) => {
  if (device === "tablet") {
    return "95.5%";
  } else if (device === "mediumMobile") {
    return "90%";
  } else if (device === "smallMobile") {
    return "100%";
  } else {
    return "100%";
  }
};
